/**
 * Seeded aliases and staff rules.
 *
 * These are *starting points* that a manager can edit or delete. Nothing here
 * is consulted by name inside the scheduler - the scheduler only ever reads the
 * generic StaffRule fields, so any of these seeds could be recreated for a
 * different employee from the Staff rules panel.
 */

import { createAliasBook, createStaffRule } from '../core/model.js';

/** Full rota name -> short name used in the Discord lunch message. */
export const SEED_ALIASES = {
  'alicia-brennan': 'Alicia',
  'cameron-ross': 'Cameron',
  'caroline-bentley': 'Cags',
  'cathy-robinson': 'Cathy',
  'clarissa-adair': 'Clarissa',
  'emma-irwin': 'Emma',
  'finn-robinson': 'Finn',
  'george-pitts': 'George',
  'glenda-black': 'Glen',
  'grace-valentine': 'Grace',
  'heather-lynas': 'Heather',
  'jack-gore': 'Jack',
  'jill-mackenzie': 'Jill',
  'jonny-miller': 'Jonny',
  'josh-maunder': 'Josh',
  'leon-geraghty': 'Leon',
  'luke-lyons': 'Luke',
  'neil-toner': 'Neil',
  'rachel-mckinley': 'Rachel',
  'rebekah-forde': 'Rebekah',
  'rona-bryson': 'Rona',
  'ronan-garrett': 'Ronan',
  'ryan-mcclelland': 'Ryan',
  'tanya-gore': 'Tanya',
  'william-wilson': 'William',
};

/**
 * Historical short-name resolution records. Cags was originally only a likely
 * match, then confirmed by Josh as Caroline Bentley.
 */
export const SEED_UNRESOLVED_ALIASES = [
  {
    token: 'Cags',
    suggestions: [
      {
        employeeId: 'caroline-bentley',
        confidence: 'confirmed',
        reason:
          'Caroline Bentley is the only person rostered on Wednesday 29 July with no lunch in '
          + 'the historical list, and her 10:00-18:00 shift lines up exactly with the 14:30 slot '
          + 'that "Cags" occupies.',
      },
    ],
    resolvedTo: 'caroline-bentley',
    note: 'Confirmed by Josh: Cags is Caroline Bentley.',
  },
];

/**
 * Seeded staff rules.
 *
 * Alicia's exception is expressed entirely as data. `forceLunchMinimumMinutes`
 * of 360 means "give her a lunch whenever the shift is six hours or longer",
 * which covers both her 08:30-14:30 and 08:00-14:00 patterns while the company
 * default for six-hour shifts remains "no lunch".
 */
export const SEED_STAFF_RULES = [
  {
    employeeId: 'alicia-brennan',
    label: 'Alicia Brennan',
    forceLunchMinimumMinutes: 360,
    preferredLunchTime: '12:30',
    priority: 'early',
    allowLaterWhenBusy: true,
    notes:
      'Agreed exception: Alicia takes a lunch on her six-hour shifts (08:30-14:30 and '
      + '08:00-14:00). She prefers an earlier lunch around 12:30, but may be moved later when '
      + 'coverage requires it.',
    seeded: true,
  },
  {
    employeeId: 'rachel-mckinley',
    label: 'Rachel McKinley',
    preferredLunchTime: '14:30',
    // Scoped to the shift the 14:30 was agreed about. Unscoped it also fired on
    // her 07:00-15:00 Saturday, where it clamped to 13:30 - an hour before the
    // end of an eight-hour shift, and behind people who started later than her.
    appliesToShifts: ['09:00-21:00'],
    priority: 'balanced',
    allowLaterWhenBusy: true,
    notes:
      'Confirmed by Josh: Rachel works 09:00-21:00 on Monday and should take lunch around 14:30. '
      + 'On any other shift she follows the normal shift ladder.',
    seeded: true,
  },
  {
    employeeId: 'rebekah-forde',
    label: 'Rebekah Forde',
    preferredLunchTime: '15:00',
    earliestLunchTime: '15:00',
    latestLunchTime: '15:30',
    // Named explicitly, though the note says "whenever she is working": both
    // are twelve-hour shifts, and a 15:00 floor on a shorter one would be as
    // wrong for her as 14:30 was for Rachel.
    appliesToShifts: ['08:00-20:00', '09:00-21:00'],
    priority: 'balanced',
    allowLaterWhenBusy: true,
    notes:
      'Confirmed by Josh: Rebekah works 08:00-20:00 or 09:00-21:00 shifts and must be placed '
      + 'at 15:00 or 15:30 on them. A shorter shift follows the normal shift ladder.',
    seeded: true,
  },
  {
    employeeId: 'rona-bryson',
    label: 'Rona Bryson',
    preferredLunchTimesByDay: { tue: '13:30' },
    priority: 'balanced',
    allowLaterWhenBusy: true,
    notes: 'Confirmed by Josh: Rona should share the 13:30 Tuesday slot when coverage allows.',
    seeded: true,
  },
  {
    employeeId: 'josh-maunder',
    label: 'Josh Maunder',
    preferredLunchTimesByDay: { tue: '14:00' },
    priority: 'balanced',
    allowLaterWhenBusy: true,
    notes: 'Confirmed Tuesday ordering: the 10:00-18:00 shift takes the 14:00 slot.',
    seeded: true,
  },
  {
    employeeId: 'cathy-robinson',
    label: 'Cathy Robinson',
    preferredLunchTimesByDay: { tue: '14:30' },
    priority: 'balanced',
    allowLaterWhenBusy: true,
    notes: 'Confirmed Tuesday ordering: the following 10:00-18:00 shift takes 14:30.',
    seeded: true,
  },
  {
    employeeId: 'roma-mccausland',
    label: 'Roma McCausland',
    excludeFromLunch: true,
    notes:
      'Roma does not appear in any historical lunch list despite working 06:30-16:00 every '
      + 'weekday. Modelled as an explicit exclusion - remove this rule if that is no longer '
      + 'correct.',
    seeded: true,
  },
];

/** User-confirmed corrections to the source rota, applied immediately on import. */
export const CONFIRMED_ROTA_CORRECTIONS = [
  {
    weekCommencing: '2026-07-27',
    employeeId: 'rachel-mckinley',
    dayKey: 'mon',
    value: '09:00-21:00',
    note: 'Josh confirmed Rachel works 09:00-21:00 on Monday despite the PDF showing ABSENT.',
  },
];

/** Return a corrected copy; the parsed source object is never mutated. */
export function applyConfirmedRotaCorrections(rota) {
  if (!rota?.rows?.length) return rota;
  let rows = rota.rows;
  for (const correction of CONFIRMED_ROTA_CORRECTIONS) {
    if (correction.weekCommencing !== rota.weekCommencing) continue;
    rows = rows.map((row) => (row.employeeId === correction.employeeId
      ? {
        ...row,
        cells: { ...row.cells, [correction.dayKey]: correction.value },
      }
      : row));
  }
  return rows === rota.rows ? rota : { ...rota, rows };
}

/** Build the seeded alias book. */
export function buildSeedAliasBook() {
  return createAliasBook(SEED_ALIASES, SEED_UNRESOLVED_ALIASES);
}

/** Build the seeded staff rules as normalised StaffRule objects. */
export function buildSeedStaffRules() {
  return SEED_STAFF_RULES.map((rule) => createStaffRule(rule));
}
